import { useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { QRCodeSVG } from "qrcode.react";
import { ArrowLeft, CalendarPlus, HandHeart } from "lucide-react";
import { ApiError } from "../api/client";
import { updateProfile } from "../api/auth";
import {
  cancelBooking,
  devPay,
  ensureQris,
  fetchBooking,
  fetchBookings,
  type BookingDetail,
  type BookingListItem,
} from "../api/bookings";
import { fetchTenants } from "../api/catalog";
import { useAuth } from "../hooks/useAuth";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  PageHeader,
  SelectField,
  Spinner,
} from "../components/ui";
import { BookingReviewCard } from "../components/BookingReviewCard";
import { ChangePasswordCard } from "../components/ChangePasswordCard";
import { GENDER_OPTIONS } from "../components/gender";
import { StarDisplay } from "../components/rating";
import { StatusPill } from "../components/status";
import { formatDateLong, formatDateShort, formatIDR, greeting } from "../lib/format";
import type { Gender, TenantPublic } from "../api/types";

function formatDateTimeLocal(iso: string | null | undefined): string {
  if (!iso) return "-";
  const d = new Date(iso);
  return `${d.toLocaleDateString("id-ID")} ${d.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })}`;
}

function BookingCard({ item }: { item: BookingListItem }) {
  return (
    <Link to={`/pasien/appointments/${item.id}`} className="card list-row">
      <div className="list-row-top">
        <span className="list-row-time">
          {item.startTime}–{item.endTime}
        </span>
        <StatusPill kind="booking" value={item.status} />
      </div>
      <div className="list-row-main">
        <p>
          <strong>{item.service.name}</strong> · {item.tenant.name}
        </p>
        <p className="muted-line">
          {formatDateLong(item.bookingDate)} · {item.therapist.name}
        </p>
        <p className="muted-line">
          {formatIDR(item.priceSnapshot)}
          {item.payment ? (
            <>
              {" · "}
              <StatusPill kind="payment" value={item.payment.status} />
            </>
          ) : null}
        </p>
        {item.payOnArrival ? (
          <p className="muted-line" style={{ margin: 0 }}>
            💵 Bayar di tempat saat tiba
          </p>
        ) : null}
        {item.review ? (
          <p className="muted-line" style={{ margin: 0 }}>
            <StarDisplay rating={item.review.rating} label="Penilaian Anda" />
          </p>
        ) : item.status === "COMPLETED" ? (
          <p className="muted-line" style={{ margin: 0 }}>
            ⭐ Belum dinilai — beri bintang untuk sesi ini.
          </p>
        ) : null}
      </div>
    </Link>
  );
}

export function PatientHomePage() {
  const { user } = useAuth();
  const bookingsQuery = useQuery({
    queryKey: ["bookings", "upcoming"],
    queryFn: () => fetchBookings("upcoming"),
  });
  // Semua janji temu yang akan datang ditampilkan di Beranda (tanpa dipotong).
  const upcoming = bookingsQuery.data ?? [];

  return (
    <div>
      <PageHeader
        title={`${greeting()}, ${user?.fullName.split(" ")[0] ?? "Pasien"}!`}
        subtitle="Kelola janji temu totok Anda."
      />
      <div style={{ display: "flex", gap: 12, marginBottom: 18 }}>
        <Link to="/pasien/booking" className="btn btn-primary">
          <CalendarPlus size={18} /> Booking Baru
        </Link>
      </div>

      <h3>Janji temu berikutnya</h3>
      {bookingsQuery.isPending ? (
        <Spinner />
      ) : bookingsQuery.isError ? (
        <ErrorState onRetry={() => void bookingsQuery.refetch()} />
      ) : upcoming.length === 0 ? (
        <EmptyState
          title="Belum ada appointment."
          hint="Cari lokasi dan buat booking pertama Anda."
        />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {upcoming.map((b) => (
            <BookingCard key={b.id} item={b} />
          ))}
        </div>
      )}
    </div>
  );
}

/** Halaman "Booking" pasien: pilih lokasi → wizard di /t/:slug */
export function PatientBookingPage() {
  const tenantsQuery = useQuery({
    queryKey: ["tenants"],
    queryFn: fetchTenants,
  });
  return (
    <div>
      <PageHeader title="Booking Baru" subtitle="Pilih lokasi layanan terlebih dahulu." />
      {tenantsQuery.isPending ? (
        <Spinner />
      ) : tenantsQuery.isError ? (
        <ErrorState onRetry={() => void tenantsQuery.refetch()} />
      ) : (
        <div className="tenant-grid">
          {tenantsQuery.data?.map((t: TenantPublic) => (
            <Link key={t.id} to={`/t/${t.slug}`} className="card tenant-card">
              <div className="tenant-card-head">
                <span className="tenant-logo" aria-hidden>
                  {t.name.charAt(0)}
                </span>
                <div>
                  <h3 style={{ margin: 0 }}>{t.name}</h3>
                  <p className="muted" style={{ margin: "2px 0 0" }}>
                    {t.address}
                  </p>
                </div>
              </div>
              <p className="tenant-hours">
                Buka {t.openingTime}–{t.closingTime}
              </p>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function PatientAppointmentsPage() {
  const bookingsQuery = useQuery({
    queryKey: ["bookings", "upcoming"],
    queryFn: () => fetchBookings("upcoming"),
  });
  return (
    <div>
      <PageHeader title="Janji Temu" subtitle="Appointment yang akan datang." />
      {bookingsQuery.isPending ? (
        <Spinner />
      ) : bookingsQuery.isError ? (
        <ErrorState onRetry={() => void bookingsQuery.refetch()} />
      ) : (bookingsQuery.data?.length ?? 0) === 0 ? (
        <EmptyState title="Belum ada appointment." hint="Booking baru lewat halaman Booking." />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {bookingsQuery.data?.map((b) => (
            <BookingCard key={b.id} item={b} />
          ))}
        </div>
      )}
    </div>
  );
}

export function PatientHistoryPage() {
  const bookingsQuery = useQuery({
    queryKey: ["bookings", "history"],
    queryFn: () => fetchBookings("history"),
  });
  return (
    <div>
      <PageHeader title="Riwayat" subtitle="Booking yang sudah selesai atau dibatalkan." />
      {bookingsQuery.isPending ? (
        <Spinner />
      ) : bookingsQuery.isError ? (
        <ErrorState onRetry={() => void bookingsQuery.refetch()} />
      ) : (bookingsQuery.data?.length ?? 0) === 0 ? (
        <EmptyState title="Belum ada riwayat." />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {bookingsQuery.data?.map((b) => (
            <BookingCard key={b.id} item={b} />
          ))}
        </div>
      )}
    </div>
  );
}

export function PatientBookingDetailPage() {
  const { id = "" } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const detailQuery = useQuery({
    queryKey: ["booking", id],
    queryFn: () => fetchBooking(id),
    enabled: !!id,
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ["booking", id] });

  async function onCancel() {
    if (!window.confirm("Batalkan booking ini? Slot akan dilepas.")) return;
    setBusy(true);
    setMessage(null);
    try {
      await cancelBooking(id);
      await queryClient.invalidateQueries({ queryKey: ["booking", id] });
      await queryClient.invalidateQueries({ queryKey: ["bookings"] });
    } catch (err) {
      setMessage(err instanceof ApiError ? err.message : "Gagal membatalkan booking.");
    } finally {
      setBusy(false);
    }
  }

  if (detailQuery.isPending) return <Spinner />;
  if (detailQuery.isError || !detailQuery.data) {
    return <ErrorState onRetry={() => void detailQuery.refetch()} />;
  }
  const b: BookingDetail = detailQuery.data;
  const cancellable = b.status === "PENDING_PAYMENT" || b.status === "CONFIRMED";
  const pay = b.status === "PENDING_PAYMENT" && b.payment?.status === "PENDING";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <Link to="/pasien/appointments" className="back-link">
        <ArrowLeft size={16} /> Kembali
      </Link>
      <PageHeader
        title={b.bookingCode}
        subtitle={`${formatDateLong(b.bookingDate)} · ${b.startTime}–${b.endTime}`}
        action={<StatusPill kind="booking" value={b.status} />}
      />

      {message ? <p className="alert alert-error">{message}</p> : null}

      <Card className="detail-lines">
        <div className="detail-line">
          <span className="k">Layanan</span>
          <span className="vv">{b.service.name}</span>
        </div>
        <div className="detail-line">
          <span className="k">Terapis</span>
          <span className="vv">
            {b.therapist.name}
            <br />
            <span className="muted-line">Dipilihkan sistem secara adil</span>
          </span>
        </div>
        <div className="detail-line">
          <span className="k">Lokasi</span>
          <span className="vv">
            {b.tenant.name}
            <br />
            {b.tenant.address ?? ""}
          </span>
        </div>
        <div className="detail-line">
          <span className="k">Harga</span>
          <span className="vv">{formatIDR(b.priceSnapshot)}</span>
        </div>
        {b.payOnArrival ? (
          <div className="detail-line">
            <span className="k">Metode bayar</span>
            <span className="vv">Bayar di tempat (tunai saat tiba)</span>
          </div>
        ) : null}
        {b.payment ? (
          <div className="detail-line">
            <span className="k">Pembayaran</span>
            <span className="vv">
              {b.payment.paymentCode} <StatusPill kind="payment" value={b.payment.status} />
            </span>
          </div>
        ) : null}
      </Card>

      <Card className="qr-wrap">
        <QRCodeSVG value={b.bookingCode} size={140} />
        <p className="muted" style={{ margin: 0 }}>
          Tunjukkan kode ini saat check-in
        </p>
      </Card>

      <BookingReviewCard booking={b} />

      {pay ? (
        <Button onClick={() => navigate(`/pasien/pay/${b.id}`)} className="btn-block">
          Bayar Sekarang
        </Button>
      ) : null}
      {cancellable && b.status === "CONFIRMED" ? (
        <Button variant="danger" onClick={() => void onCancel()} loading={busy}>
          Batalkan Booking
        </Button>
      ) : null}
      {b.status === "PENDING_PAYMENT" ? (
        <Button variant="danger" onClick={() => void onCancel()} loading={busy}>
          Batalkan Booking
        </Button>
      ) : null}
      <p className="muted-line" style={{ margin: 0 }}>
        Dibuat {formatDateTimeLocal(b.createdAt)} · {b.tenant.phone ? `HP ${b.tenant.phone}` : ""}
      </p>
      <button type="button" className="btn btn-ghost" onClick={refresh}>
        Muat ulang status
      </button>
    </div>
  );
}

export function PatientPayPage() {
  const { bookingId = "" } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const isDev =
    window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";

  const detailQuery = useQuery({
    queryKey: ["booking", bookingId],
    queryFn: () => fetchBooking(bookingId),
    enabled: !!bookingId,
    refetchInterval: (query) => {
      const b = query.state.data as BookingDetail | undefined;
      return b?.status === "PENDING_PAYMENT" && b.payment?.status === "PENDING" ? 5000 : false;
    },
  });

  async function run(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
      await queryClient.invalidateQueries({ queryKey: ["booking", bookingId] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Terjadi kesalahan. Coba lagi.");
    }
  }

  if (detailQuery.isPending) return <Spinner />;
  if (detailQuery.isError || !detailQuery.data) {
    return <ErrorState onRetry={() => void detailQuery.refetch()} />;
  }
  const b: BookingDetail = detailQuery.data;
  const payment = b.payment;

  if (b.status === "CONFIRMED" || payment?.status === "PAID") {
    return (
      <div className="state-box">
        <div className="state-icon" aria-hidden>
          ✅
        </div>
        <p className="state-title">Pembayaran berhasil!</p>
        <p className="state-hint">
          Booking {b.bookingCode} terkondirmasi untuk {formatDateLong(b.bookingDate)} pukul{" "}
          {b.startTime}.
        </p>
        <Button onClick={() => navigate("/pasien/appointments")}>Lihat Janji Temu</Button>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <Link to={`/pasien/appointments/${b.id}`} className="back-link">
        <ArrowLeft size={16} /> Detail booking
      </Link>
      <PageHeader
        title="Bayar via QRIS"
        subtitle={`${b.bookingCode} · ${formatDateShort(b.bookingDate)} ${b.startTime} · ${b.tenant.name}`}
        action={<StatusPill kind="booking" value={b.status} />}
      />

      {error ? <p className="alert alert-error">{error}</p> : null}

      {b.status === "PENDING_PAYMENT" && payment ? (
        <>
          <Card className="qr-wrap">
            {payment.qrContent ? (
              <QRCodeSVG value={payment.qrContent} size={220} />
            ) : (
              <p className="state-hint">QR belum tersedia. Muat ulang.</p>
            )}
            <p style={{ fontWeight: 800, fontSize: 20, margin: 0 }}>{formatIDR(payment.amount)}</p>
            <p className="muted" style={{ margin: 0 }}>
              Kode: {payment.paymentCode} · Berakhir {formatDateTimeLocal(payment.expiresAt)}
            </p>
          </Card>
          <div className="pay-actions">
            <Button
              onClick={() =>
                void run(async () => {
                  await queryClient.refetchQueries({ queryKey: ["booking", bookingId] });
                })
              }
            >
              Saya sudah membayar
            </Button>
            {payment.status === "PENDING" ? (
              <Button variant="outline" onClick={() => void run(() => ensureQris(bookingId))}>
                Perbarui QR
              </Button>
            ) : null}
            {isDev ? (
              <Button variant="ghost" onClick={() => void run(() => devPay(payment.paymentCode))}>
                Simulasi bayar (dev)
              </Button>
            ) : null}
          </div>
          <p className="muted-line">
            Scan QR dengan aplikasi pembayaran (GoPay, OVO, DANA, m-Banking, dll). Status diperbarui
            otomatis.
          </p>
        </>
      ) : (
        <EmptyState
          title="Booking tidak menunggu pembayaran."
          hint={`Status saat ini: ${b.status}.`}
        />
      )}
    </div>
  );
}

export function PatientProfilePage() {
  const { user, me, logoutNow } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [gender, setGender] = useState<Gender | "">(user?.gender ?? "");
  const [savingGender, setSavingGender] = useState(false);
  const [genderError, setGenderError] = useState<string | null>(null);
  const [genderSaved, setGenderSaved] = useState(false);

  async function onLogout() {
    setBusy(true);
    try {
      await logoutNow();
      navigate("/login", { replace: true });
    } finally {
      setBusy(false);
    }
  }

  async function onSubmitGender(e: FormEvent) {
    e.preventDefault();
    setGenderError(null);
    setGenderSaved(false);
    if (!gender) {
      setGenderError("Pilih jenis kelamin dulu.");
      return;
    }
    setSavingGender(true);
    try {
      await updateProfile({ gender });
      await queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
      setGenderSaved(true);
    } catch (err) {
      setGenderError(err instanceof ApiError ? err.message : "Gagal menyimpan. Coba lagi.");
    } finally {
      setSavingGender(false);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <PageHeader title="Profil" />
      <Card className="detail-lines">
        <div className="detail-line">
          <span className="k">Nama</span>
          <span className="vv">{user?.fullName}</span>
        </div>
        <div className="detail-line">
          <span className="k">Email</span>
          <span className="vv">{user?.email}</span>
        </div>
        <div className="detail-line">
          <span className="k">Peran</span>
          <span className="vv">Pasien</span>
        </div>
        <div className="detail-line">
          <span className="k">Jenis kelamin</span>
          <span className="vv">
            {user?.gender === "L" ? "Laki-laki" : user?.gender === "P" ? "Perempuan" : "Belum diisi"}
          </span>
        </div>
        {me?.tenant ? (
          <div className="detail-line">
            <span className="k">Lokasi</span>
            <span className="vv">{me.tenant.name}</span>
          </div>
        ) : null}
      </Card>

      <Card>
        <form onSubmit={onSubmitGender} className="form-stack">
          <SelectField
            label="Jenis kelamin"
            name="gender"
            required
            options={GENDER_OPTIONS}
            placeholder="Pilih jenis kelamin"
            value={gender}
            onChange={(e) => {
              setGender(e.target.value as Gender | "");
              setGenderSaved(false);
            }}
          />
          <p className="state-hint" style={{ margin: 0 }}>
            Sistem memilih terapis dengan jenis kelamin yang sama dengan Anda. Bila tidak ada yang
            bertugas pada jam tersebut, silakan pilih jam atau tanggal lain.
          </p>
          {genderError ? <p className="alert alert-error">{genderError}</p> : null}
          {genderSaved ? (
            <p className="state-hint" style={{ margin: 0 }}>
              ✅ Jenis kelamin tersimpan.
            </p>
          ) : null}
          <Button type="submit" loading={savingGender}>
            Simpan
          </Button>
        </form>
      </Card>

      <ChangePasswordCard />
      <Button variant="danger" onClick={() => void onLogout()} loading={busy}>
        Keluar
      </Button>
      <p className="state-hint">
        <HandHeart size={14} /> Butuh bantuan? Hubungi nomor telepon yang tertera di detail booking
        Anda.
      </p>
    </div>
  );
}
